import { useBlocker } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { draftsApi, DraftItem } from "../api/drafts";
import { MailWorkspace } from "../components/MailWorkspace";
import { DraftCard } from "../components/DraftCard";
import { AppLayout } from "../components/AppLayout";
import { FileEdit, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast as sonnerToast } from "sonner";

export function DraftsPage() {
  const queryClient = useQueryClient();
  const [dirty, setDirty] = useState(false);

  const {
    data: drafts,
    isLoading,
    error,
  } = useQuery<DraftItem[]>({
    queryKey: ["drafts"],
    queryFn: draftsApi.list,
    refetchInterval: 30000,
  });

  const approveMutation = useMutation({
    mutationFn: draftsApi.approve,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["drafts"] });
      sonnerToast.success("Draft approvato e email inviata!");
    },
    onError: () => {
      sonnerToast.error("Errore durante l'approvazione del draft");
    },
  });

  const editMutation = useMutation<
    { message: string; draft: string },
    Error,
    { emailId: string; body: string }
  >({
    mutationFn: ({ emailId, body }) => draftsApi.edit(emailId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["drafts"] });
      sonnerToast.success("Draft modificato con successo");
    },
    onError: () => {
      sonnerToast.error("Errore durante la modifica del draft");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: draftsApi.reject,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["drafts"] });
      sonnerToast.info("Draft rifiuto, email tornata in coda");
    },
    onError: () => {
      sonnerToast.error("Errore durante il rifiuto del draft");
    },
  });

  const blocker = useBlocker(
    dirty ||
      approveMutation.isPending ||
      editMutation.isPending ||
      rejectMutation.isPending,
  );
  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (
      approveMutation.isPending ||
      editMutation.isPending ||
      rejectMutation.isPending
    ) {
      blocker.reset();
      sonnerToast.info("Attendi il completamento dell’operazione.");
      return;
    }
    if (window.confirm("Hai modifiche non salvate. Vuoi abbandonarle?")) {
      setDirty(false);
      blocker.proceed();
    } else blocker.reset();
  }, [
    blocker,
    approveMutation.isPending,
    editMutation.isPending,
    rejectMutation.isPending,
  ]);

  const handleApprove = useCallback(
    (emailId: string) => {
      approveMutation.mutate(emailId);
    },
    [approveMutation],
  );

  const handleEdit = useCallback(
    (emailId: string, body: string) => {
      return editMutation.mutateAsync({ emailId, body });
    },
    [editMutation],
  );

  const handleReject = useCallback(
    (emailId: string) => {
      rejectMutation.mutate(emailId);
    },
    [rejectMutation],
  );

  const draftCount = drafts?.length || 0;

  return (
    <AppLayout
      breadcrumbs={[{ label: "Bozze" }]}
      logoIcon={<FileEdit className="size-4 text-primary" />}
      topbarActions={
        <>
          <span className="text-sm text-muted-foreground mr-2">
            {draftCount} draft in attesa
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["drafts"] })
            }
            disabled={isLoading}
          >
            <RefreshCw className="size-3.5" />
            Aggiorna
          </Button>
        </>
      }
      mainClassName="mail-main"
    >
      <MailWorkspace
        title="Bozze"
        selectionKey="draft"
        loading={isLoading}
        error={!!error}
        onRetry={() => {
          void queryClient.invalidateQueries({ queryKey: ["drafts"] });
        }}
        empty="Nessuna bozza in attesa"
        canLeave={() => {
          if (
            approveMutation.isPending ||
            editMutation.isPending ||
            rejectMutation.isPending
          ) {
            sonnerToast.info("Attendi il completamento dell’operazione.");
            return false;
          }
          if (!dirty) return true;
          if (!window.confirm("Hai modifiche non salvate. Vuoi abbandonarle?"))
            return false;
          setDirty(false);
          return true;
        }}
        items={(drafts || []).map((draft) => ({
          id: draft.email_id,
          sender: draft.from_email,
          title: draft.subject || "Senza oggetto",
          preview: draft.draft_body,
          date: draft.received_at,
          priority: draft.priority,
          meta: "Da revisionare",
        }))}
        renderDetail={(id) => {
          const draft = drafts!.find((item) => item.email_id === id)!;
          return (
            <DraftCard
              {...draft}
              onApprove={handleApprove}
              onEdit={handleEdit}
              onReject={handleReject}
              pending={
                approveMutation.isPending ||
                rejectMutation.isPending ||
                editMutation.isPending
              }
              onDirtyChange={setDirty}
            />
          );
        }}
      />
    </AppLayout>
  );
}
